import React from "react";

/**
 * Brand-accurate channel icons for LeadifyCRM Inbox.
 * Each channel has a distinct silhouette AND a distinct hue so the
 * user can visually differentiate at-a-glance in the conversation list.
 *
 * Usage:
 *   <ChannelIcon channel="whatsapp" size={14} />
 *   <ChannelIcon channel="chat" size={14} muted />
 */

// -- Individual glyphs (viewBox 24, single-color fills so we can tint via `color`) ---

const glyphs = {
  chat: (
    // Rounded speech bubble with dots — distinct from WhatsApp's tail
    <path d="M4 5c0-1.1.9-2 2-2h12a2 2 0 012 2v9a2 2 0 01-2 2H9l-4 4v-4H6a2 2 0 01-2-2V5zm4 3.5a1 1 0 100 2 1 1 0 000-2zm4 0a1 1 0 100 2 1 1 0 000-2zm4 0a1 1 0 100 2 1 1 0 000-2z" fill="currentColor" />
  ),
  email: (
    // Classic envelope
    <path d="M3 6.5A2.5 2.5 0 015.5 4h13A2.5 2.5 0 0121 6.5v11a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 17.5v-11zm2.5-.5a.5.5 0 00-.5.5v.4l7 4.375L19 6.9V6.5a.5.5 0 00-.5-.5h-13zm13.5 3.235l-6.472 4.045a1 1 0 01-1.056 0L5 9.235V17.5a.5.5 0 00.5.5h13a.5.5 0 00.5-.5V9.235z" fill="currentColor" />
  ),
  whatsapp: (
    // Official WhatsApp logo silhouette (public brand shape)
    <>
      <path d="M12.04 2C6.55 2 2.08 6.47 2.08 11.96c0 1.75.46 3.46 1.32 4.97L2 22l5.19-1.36a9.94 9.94 0 004.85 1.24h.01c5.49 0 9.96-4.47 9.97-9.96 0-2.66-1.03-5.17-2.92-7.05A9.9 9.9 0 0012.04 2zm0 18.15h-.01a8.29 8.29 0 01-4.21-1.15l-.3-.18-3.13.82.84-3.05-.2-.31a8.24 8.24 0 01-1.27-4.41c0-4.57 3.72-8.29 8.3-8.29 2.22 0 4.3.86 5.86 2.43a8.23 8.23 0 012.42 5.87c0 4.57-3.72 8.28-8.3 8.28z" fill="currentColor"/>
      <path d="M16.6 14.29c-.25-.13-1.47-.72-1.69-.81-.23-.08-.39-.13-.56.13-.16.25-.64.81-.79.98-.15.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23a7.51 7.51 0 01-1.38-1.72c-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.44.13-.15.16-.25.25-.42.08-.16.04-.31-.02-.44-.06-.13-.55-1.34-.76-1.83-.2-.48-.4-.42-.55-.42-.14 0-.31-.02-.47-.02-.16 0-.42.06-.64.31s-.85.83-.85 2.02.87 2.34.99 2.5c.13.16 1.7 2.61 4.13 3.66.58.25 1.03.4 1.38.51.58.19 1.11.16 1.53.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.08.15-1.18-.07-.11-.24-.17-.5-.29z" fill="currentColor"/>
    </>
  ),
  facebook: (
    // Facebook "f" — inside rounded square, distinctive
    <path d="M13.5 21v-7.5h2.5l.4-3H13.5V8.6c0-.87.24-1.46 1.48-1.46H16.5V4.42c-.27-.04-1.2-.12-2.28-.12-2.25 0-3.79 1.37-3.79 3.89V10.5H8v3h2.43V21h3.07z" fill="currentColor"/>
  ),
  google_lead: (
    <path d="M21.35 11.1H12.2v2.98h5.27c-.23 1.4-1.66 4.1-5.27 4.1-3.17 0-5.76-2.63-5.76-5.87s2.59-5.87 5.76-5.87c1.8 0 3.01.77 3.7 1.43l2.52-2.43C16.8 3.94 14.7 3 12.2 3 7.1 3 3 7.1 3 12.2s4.1 9.2 9.2 9.2c5.31 0 8.83-3.73 8.83-8.99 0-.6-.07-1.06-.15-1.51z" fill="currentColor"/>
  ),
  instagram: (
    // Instagram camera outline — squared with lens
    <>
      <path d="M17.5 2h-11A4.5 4.5 0 002 6.5v11A4.5 4.5 0 006.5 22h11a4.5 4.5 0 004.5-4.5v-11A4.5 4.5 0 0017.5 2zm2.5 15.5A2.5 2.5 0 0117.5 20h-11A2.5 2.5 0 014 17.5v-11A2.5 2.5 0 016.5 4h11A2.5 2.5 0 0120 6.5v11z" fill="currentColor"/>
      <path d="M12 7a5 5 0 100 10 5 5 0 000-10zm0 8a3 3 0 110-6 3 3 0 010 6z" fill="currentColor"/>
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor"/>
    </>
  ),
};

// Brand color per channel — muted variants for filter chips when inactive.
export const CHANNEL_COLORS = {
  chat:      { color: "#38BDF8", bg: "rgba(56,189,248,0.12)",  border: "rgba(56,189,248,0.3)" },   // sky blue
  email:     { color: "#FBBF24", bg: "rgba(251,191,36,0.12)",  border: "rgba(251,191,36,0.3)" },   // amber
  whatsapp:  { color: "#25D366", bg: "rgba(37,211,102,0.14)",  border: "rgba(37,211,102,0.35)" },  // WhatsApp green
  facebook:  { color: "#1877F2", bg: "rgba(24,119,242,0.14)",  border: "rgba(24,119,242,0.35)" },  // Facebook blue
  google_lead: { color: "#EA4335", bg: "rgba(234,67,53,0.14)",  border: "rgba(234,67,53,0.35)" },
  instagram: { color: "#E1306C", bg: "rgba(225,48,108,0.14)",  border: "rgba(225,48,108,0.35)" },  // Instagram pink
};

export const channelLabel = (c) => (c === "google_lead" ? "Google Lead" : c);

export default function ChannelIcon({ channel, size = 14, className = "", muted = false, badge = false }) {
  const key = String(channel || "").toLowerCase();
  const glyph = glyphs[key] || glyphs.chat;
  const c = CHANNEL_COLORS[key] || CHANNEL_COLORS.chat;
  const color = muted ? "currentColor" : c.color;

  if (badge) {
    // Rounded square badge (used in Inbox conversation list — brand background + brand icon)
    return (
      <span
        className={`inline-flex items-center justify-center rounded-sm ${className}`}
        style={{ backgroundColor: c.bg, border: `1px solid ${c.border}`, width: size + 8, height: size + 8, color }}
        aria-label={key}
      >
        <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ color }}>{glyph}</svg>
      </span>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ color }}
      aria-label={key}
      data-testid={`channel-icon-${key}`}
    >
      {glyph}
    </svg>
  );
}
